import { Box, Popper, Typography } from '@mui/material';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentType,
} from 'react';
import { createPortal } from 'react-dom';
import { TOOLTIP_Z_INDEX } from '../../../features/Recorder/styles/recorderStyles';
import {
  createRichTextEditorStyles,
  richTextEditorStyles,
} from '../styles/richTextEditorStyles';
import {
  findAdjacentMacro,
  findMacroAncestor,
  isMacroElement,
  sanitizeHtml,
} from '../utils/macroUtils';
import {
  findAdjacentTable,
  findTableAncestor,
  isTablePlaceholder,
  replaceTablesWithPlaceholders,
  restoreTablesFromPlaceholders,
} from '../utils/tableUtils';

interface RichTextEditorProps {
  value: string; // HTML string
  onChange: (value: string) => void;
  onBlur: () => void;
  scaleFontSize?: (fontSize: string) => string;
  readonly?: boolean;
}

const MACRO_TOOLTIP_TEXT =
  "Macros can't be manually edited here. Replace to chart to edit Macros.";

const TABLE_TOOLTIP_TEXT =
  "Tables can't be viewed or edited here. Replace to chart to view/edit tables.";

export const RichTextEditor: ComponentType<RichTextEditorProps> = ({
  value,
  onChange,
  onBlur,
  scaleFontSize = (size) => size,
  readonly = false,
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipAnchor, setTooltipAnchor] = useState<HTMLElement | null>(null);
  const [tooltipText, setTooltipText] = useState(MACRO_TOOLTIP_TEXT);
  const isComposingRef = useRef(false);
  const lastValueRef = useRef<string>('');

  useEffect(() => {
    if (!editorRef.current) return;

    // Avoid updating if the value is what we just sent via onChange
    if (value === lastValueRef.current) return;

    const currentContent = editorRef.current.innerHTML;
    if (currentContent === value) return;

    const sanitized = sanitizeHtml(value);
    editorRef.current.innerHTML = sanitized;
    lastValueRef.current = value;

    // Replace tables with placeholders
    replaceTablesWithPlaceholders(editorRef.current);

    // Reset default browser margins on paragraph elements
    const paragraphs = editorRef.current.querySelectorAll('p');
    paragraphs.forEach((p) => {
      const element = p as HTMLElement;
      element.style.margin = '0';
      element.style.marginBlockStart = '0';
      element.style.marginBlockEnd = '0';
      element.style.marginInlineStart = '0';
      element.style.marginInlineEnd = '0';
      element.style.marginTop = '0';
      element.style.marginBottom = '0';
      element.style.marginLeft = '0';
      element.style.marginRight = '0';
    });

    // Ensure caret visibility by adding a zero-width space after macros
    // Note: Macro styling (bold, underline) is handled via CSS attribute selectors in richTextEditorStyles
    const macros = editorRef.current.querySelectorAll(
      '[data-question], [data-field]'
    );
    macros.forEach((macro) => {
      const element = macro as HTMLElement;
      if (
        !element.nextSibling ||
        element.nextSibling.nodeType !== Node.TEXT_NODE
      ) {
        const zeroWidthSpace = document.createTextNode('\u200B');
        element.parentNode?.insertBefore(zeroWidthSpace, element.nextSibling);
      }
    });

    // Ensure caret visibility by adding a zero-width space after table placeholders
    const tablePlaceholders = editorRef.current.querySelectorAll(
      '[data-table-placeholder]'
    );
    tablePlaceholders.forEach((placeholder) => {
      const element = placeholder as HTMLElement;
      if (
        !element.nextSibling ||
        element.nextSibling.nodeType !== Node.TEXT_NODE
      ) {
        const zeroWidthSpace = document.createTextNode('\u200B');
        element.parentNode?.insertBefore(zeroWidthSpace, element.nextSibling);
      }
    });
  }, [value]);

  const resetParagraphMargins = useCallback((container: HTMLElement) => {
    const paragraphs = container.querySelectorAll('p');
    paragraphs.forEach((p) => {
      const element = p as HTMLElement;
      element.style.margin = '0';
      element.style.marginBlockStart = '0';
      element.style.marginBlockEnd = '0';
      element.style.marginInlineStart = '0';
      element.style.marginInlineEnd = '0';
      element.style.marginTop = '0';
      element.style.marginBottom = '0';
      element.style.marginLeft = '0';
      element.style.marginRight = '0';
    });
  }, []);

  const handleInput = useCallback(() => {
    if (readonly || isComposingRef.current) return;

    if (editorRef.current) {
      // Reset margins on any newly created paragraphs
      resetParagraphMargins(editorRef.current);

      const html = editorRef.current.innerHTML;
      // Track this value to prevent circular updates
      lastValueRef.current = html;
      // Keep placeholders during editing - don't restore tables yet
      onChange(html);
    }
  }, [onChange, readonly, resetParagraphMargins]);

  // Handle composition events (for IME input)
  const handleCompositionStart = useCallback(() => {
    isComposingRef.current = true;
  }, []);

  const handleCompositionEnd = useCallback(() => {
    isComposingRef.current = false;
    handleInput();
  }, [handleInput]);

  // Prevent editing inside macro elements and table placeholders
  const handleBeforeInput = useCallback(
    (e: Event) => {
      if (readonly) return;

      const inputEvent = e as InputEvent;
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);
      const container = range.commonAncestorContainer;

      const macroAncestor = findMacroAncestor(container);
      if (macroAncestor) {
        e.preventDefault();
        inputEvent.stopPropagation();
        return;
      }

      const tableAncestor = findTableAncestor(container);
      if (tableAncestor) {
        e.preventDefault();
        inputEvent.stopPropagation();
      }
    },
    [readonly]
  );

  // Handle click events - prevent cursor from entering macros and table placeholders
  const handleClick = useCallback((e: Event) => {
    const mouseEvent = e as MouseEvent;
    const target = mouseEvent.target as HTMLElement;

    if (isMacroElement(target)) {
      e.preventDefault();
      mouseEvent.stopPropagation();
      return;
    }

    if (isTablePlaceholder(target)) {
      e.preventDefault();
      mouseEvent.stopPropagation();
    }
  }, []);

  // Handle keyboard events - atomic deletion and prevent editing
  const handleKeyDown = useCallback(
    (e: Event) => {
      if (readonly) return;

      const keyboardEvent = e as KeyboardEvent;
      const { key } = keyboardEvent;

      if (key === 'Backspace' || key === 'Delete') {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);

        const macroAncestor = findMacroAncestor(range.commonAncestorContainer);
        if (macroAncestor) {
          e.preventDefault();
          keyboardEvent.stopPropagation();
          return;
        }

        const tableAncestor = findTableAncestor(range.commonAncestorContainer);
        if (tableAncestor) {
          e.preventDefault();
          keyboardEvent.stopPropagation();
          return;
        }

        // Check if we're about to delete into a macro - delete it atomically
        const adjacentMacro = findAdjacentMacro(
          range.commonAncestorContainer,
          key
        );

        if (adjacentMacro) {
          e.preventDefault();
          keyboardEvent.stopPropagation();

          // Delete the entire macro element using Selection API for proper undo support
          const selection = window.getSelection();
          if (selection) {
            const range = document.createRange();
            range.selectNode(adjacentMacro);
            selection.removeAllRanges();
            selection.addRange(range);

            // Use execCommand for undo support (deprecated but still works)
            if (document.queryCommandSupported('delete')) {
              document.execCommand('delete', false);
            } else {
              adjacentMacro.remove();
            }
          }

          handleInput();
          return;
        }

        // Check if we're about to delete into a table placeholder - delete it atomically
        const adjacentTable = findAdjacentTable(
          range.commonAncestorContainer,
          key
        );

        if (adjacentTable) {
          e.preventDefault();
          keyboardEvent.stopPropagation();

          const selection = window.getSelection();
          if (selection) {
            const range = document.createRange();
            range.selectNode(adjacentTable);
            selection.removeAllRanges();
            selection.addRange(range);

            if (document.queryCommandSupported('delete')) {
              document.execCommand('delete', false);
            } else {
              adjacentTable.remove();
            }
          }

          handleInput();
        }
      }
    },
    [readonly, handleInput]
  );

  const handleMouseMove = useCallback((e: Event) => {
    const mouseEvent = e as MouseEvent;
    const target = mouseEvent.target as HTMLElement;

    if (isMacroElement(target)) {
      setTooltipAnchor(target);
      setTooltipText(MACRO_TOOLTIP_TEXT);
      setShowTooltip(true);
    } else if (isTablePlaceholder(target)) {
      setTooltipAnchor(target);
      setTooltipText(TABLE_TOOLTIP_TEXT);
      setShowTooltip(true);
    } else {
      const macroAncestor = findMacroAncestor(target);
      if (macroAncestor) {
        setTooltipAnchor(macroAncestor);
        setTooltipText(MACRO_TOOLTIP_TEXT);
        setShowTooltip(true);
      } else {
        const tableAncestor = findTableAncestor(target);
        if (tableAncestor) {
          setTooltipAnchor(tableAncestor);
          setTooltipText(TABLE_TOOLTIP_TEXT);
          setShowTooltip(true);
        } else {
          setShowTooltip(false);
          setTooltipAnchor(null);
        }
      }
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    setShowTooltip(false);
    setTooltipAnchor(null);
  }, []);

  // Hide tooltip when the user scrolls (cursor may no longer be over the macro)
  useEffect(() => {
    if (!showTooltip) return;
    const hideTooltipOnScroll = () => {
      setShowTooltip(false);
      setTooltipAnchor(null);
    };
    window.addEventListener('scroll', hideTooltipOnScroll, true);
    return () =>
      window.removeEventListener('scroll', hideTooltipOnScroll, true);
  }, [showTooltip]);

  // Handle blur - restore tables before calling parent onBlur
  const handleBlur = useCallback(() => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      const restoredHtml = restoreTablesFromPlaceholders(html);

      if (restoredHtml !== html) {
        lastValueRef.current = restoredHtml;
        onChange(restoredHtml);
      }
    }
    onBlur();
  }, [onChange, onBlur]);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    editor.addEventListener('beforeinput', handleBeforeInput);
    editor.addEventListener('click', handleClick);
    editor.addEventListener('keydown', handleKeyDown);
    editor.addEventListener('mousemove', handleMouseMove);
    editor.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      editor.removeEventListener('beforeinput', handleBeforeInput);
      editor.removeEventListener('click', handleClick);
      editor.removeEventListener('keydown', handleKeyDown);
      editor.removeEventListener('mousemove', handleMouseMove);
      editor.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [
    handleBeforeInput,
    handleClick,
    handleKeyDown,
    handleMouseMove,
    handleMouseLeave,
  ]);

  return (
    <Box sx={richTextEditorStyles.editorWrapper}>
      <Box
        ref={editorRef}
        component='div'
        contentEditable={!readonly}
        onInput={handleInput}
        onCompositionStart={handleCompositionStart}
        onCompositionEnd={handleCompositionEnd}
        onBlur={handleBlur}
        suppressContentEditableWarning
        sx={createRichTextEditorStyles(scaleFontSize)}
        data-testid='rich-text-editor'
      />

      {/* Custom tooltip rendered at document body level for proper z-index */}
      {showTooltip &&
        tooltipAnchor &&
        createPortal(
          <Popper
            open={showTooltip}
            anchorEl={tooltipAnchor}
            placement='top'
            modifiers={[
              {
                name: 'offset',
                options: {
                  offset: [0, -10],
                },
              },
            ]}
            style={{ zIndex: TOOLTIP_Z_INDEX, pointerEvents: 'none' }}
          >
            <Box sx={richTextEditorStyles.tooltip}>
              <Typography sx={richTextEditorStyles.tooltipTypography}>
                {tooltipText}
              </Typography>
            </Box>
          </Popper>,
          document.body
        )}
    </Box>
  );
};
